// ============================================================================
// Upcoming annual increases (inflationary kickers) — dashboard widget for
// finance / executive / superuser. Buckets from v_upcoming_kickers plus the
// seeded-tranche count; everything deep-links into /money/kickers where Jose
// validates or defers. Self-fetching + RLS-scoped; gated by the registry.
// ============================================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CalendarClock, AlertTriangle } from 'lucide-react';
import { WidgetCard, WidgetSkeleton } from '../widget-card';
import { supabase } from '../../../shared/supabase';

interface Counts {
  overdue: number;
  d30: number;
  d60: number;
  d90: number;
  seeded: number;
}

export function KickersWidget({ enabled }: { enabled: boolean }) {
  const [counts, setCounts] = useState<Counts | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const { data, error: e } = await supabase
        .from('v_upcoming_kickers')
        .select('bucket, kicker_seed_pending');
      if (cancelled) return;
      if (e) {
        setError(e.message);
        return;
      }
      const rows = data ?? [];
      setCounts({
        overdue: rows.filter((r) => r.bucket === 'Overdue').length,
        d30: rows.filter((r) => r.bucket === '30').length,
        d60: rows.filter((r) => r.bucket === '60').length,
        d90: rows.filter((r) => r.bucket === '90').length,
        seeded: rows.filter((r) => r.kicker_seed_pending).length,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <WidgetCard
      title="Annual increases"
      subtitle="Upcoming inflationary kickers — validate or defer."
      minHeight={240}
    >
      {error && <p className="text-xs text-st-danger-fg">Couldn't load: {error}</p>}
      {!error && !counts && <WidgetSkeleton lines={3} />}
      {!error && counts && (
        <div className="-mx-2">
          {counts.seeded > 0 && (
            <Link
              to="/money/kickers"
              className="group flex items-center justify-between gap-3 px-2 py-2.5 hover:bg-tcs-surface"
            >
              <span className="flex min-w-0 items-center gap-2 text-sm text-tcs-text">
                <AlertTriangle size={14} className="shrink-0 text-st-warn-fg" aria-hidden />
                Seeded dates needing review
              </span>
              <span className="flex items-center gap-1">
                <span className="tabular-nums font-semibold text-tcs-text">{counts.seeded}</span>
                <ArrowUpRight size={13} className="text-tcs-text-muted group-hover:text-tcs-text-secondary" aria-hidden />
              </span>
            </Link>
          )}
          {(
            [
              ['Overdue', counts.overdue],
              ['Next 30 days', counts.d30],
              ['31–60 days', counts.d60],
              ['61–90 days', counts.d90],
            ] as const
          ).map(([label, n]) => (
            <Link
              key={label}
              to="/money/kickers"
              className="group flex items-center justify-between gap-3 border-t border-tcs-border/60 px-2 py-2.5 hover:bg-tcs-surface"
            >
              <span className="flex min-w-0 items-center gap-2 text-sm text-tcs-text">
                <CalendarClock
                  size={14}
                  className={`shrink-0 ${label === 'Overdue' && n > 0 ? 'text-st-danger-fg' : 'text-tcs-text-muted'}`}
                  aria-hidden
                />
                {label}
              </span>
              <span className="flex items-center gap-1">
                <span className="tabular-nums font-semibold text-tcs-text">{n}</span>
                <ArrowUpRight size={13} className="text-tcs-text-muted group-hover:text-tcs-text-secondary" aria-hidden />
              </span>
            </Link>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}
